import { useEffect, useRef } from 'react'
import useSWR from 'swr'
import { apiGetPreferences, apiUpdatePreferences } from '@/services/AuthService'
import { useLocaleStore } from '@/store/localeStore'
import { isSupportedLocale } from '@/configs/locale.config'
import type { UserPreferences } from '@/@types/trackheros'

/**
 * Reconciles the language stored on the account with the one in this browser.
 *
 * Renders nothing — it exists to run once per signed-in session, inside the
 * post-login tree so it only mounts when there is an account to reconcile with.
 *
 * Two directions, and which one applies is decided by whether the account has
 * a language at all:
 *
 *  - **Set on the account** → adopt it. The user chose this, on some device;
 *    that choice should follow them here. Local switches are written back
 *    (`useLocale`), so by the time this runs again the two already agree and
 *    the adopt is a no-op.
 *  - **Blank** → push what this browser guessed. The user has never chosen, so
 *    the guess is the best information anyone has, and recording it means
 *    their next device opens in the same language instead of guessing again
 *    from a different browser's settings.
 *
 * The SWR key is shared with Settings, so the preferences request is not made
 * twice and a change made there updates this cache too.
 */
const LocaleSync = () => {
    const locale = useLocaleStore((state) => state.locale)
    const adoptLocale = useLocaleStore((state) => state.adoptLocale)

    const { data } = useSWR<UserPreferences>(
        ['preferences'],
        () => apiGetPreferences(),
        { revalidateOnFocus: false },
    )

    // Reconcile once per session. Without this, a language switch made here
    // would be undone by the next revalidation of the same SWR key — the
    // server value having not yet caught up with the click.
    const reconciled = useRef(false)

    // Read through a ref so the effect can depend on `data` alone. Depending on
    // `locale` too would re-run the moment we adopt one, and the push branch
    // would then fire with the value we just took from the server.
    const localeRef = useRef(locale)
    localeRef.current = locale

    useEffect(() => {
        if (!data || reconciled.current) {
            return
        }
        reconciled.current = true

        if (isSupportedLocale(data.language)) {
            adoptLocale(data.language)
            return
        }

        apiUpdatePreferences({ language: localeRef.current }).catch(() => {
            // Best effort. Failing to record a guess costs the user nothing
            // today — the interface is already in the right language — and it
            // will be recorded properly the moment they pick one deliberately.
        })
    }, [data, adoptLocale])

    return null
}

export default LocaleSync
